import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@vp/ubik-context';
import FragmentMarker from '../../components/FragmentMarker/FragmentMarker';
import { sanitiseLocale } from '../../i18n/config';
import { isBusinessNameIdeasAvailable } from '../../utils/featureAvailability';
import updateSearchParams from '../../utils/updateSearchParams';
import { IndustryBusinessNameIdeasData } from './components/content/explore-business-ideas-data';
import Hero from './components/Hero/Hero';

const BusinessNameResults = lazy(
    () => import('./components/BusinessNameResults/BusinessNameResults'),
);
const ExploreIdeas = lazy(() => import('./components/ExploreIdeas/ExploreIdeas'));
const HowItWorks = lazy(() => import('./components/HowItWorks/HowItWorks'));
const GoogleWorkspaceBanner = lazy(
    () => import('./components/GoogleWorkspaceBanner/GoogleWorkspaceBanner'),
);

interface BusinessNameGeneratorPageProps {
    industryBusinessNameIdeasData: IndustryBusinessNameIdeasData;
}

const BusinessNameGeneratorPage = ({
    industryBusinessNameIdeasData,
}: BusinessNameGeneratorPageProps) => {
    const userContext = useUserContext();
    const searchParam = userContext.queryStrings?.search || '';
    const { i18n } = useTranslation();
    const locale = sanitiseLocale(i18n.language);
    const [showResultsPage, setShowResultsPage] = useState<boolean>(searchParam !== '');
    const [inputKeyword, setInputKeyword] = useState<string>(searchParam);

    const handleSubmit = () => {
        setShowResultsPage(true);
        updateSearchParams(inputKeyword.trim());
    };

    return showResultsPage ? (
        <Suspense>
            <BusinessNameResults inputKeyword={inputKeyword} />
        </Suspense>
    ) : (
        <>
            <FragmentMarker fragmentId="business-name-generator" />
            <Hero
                onSubmit={() => handleSubmit()}
                inputKeyword={inputKeyword}
                onSearchInputChange={(value) => setInputKeyword(value)}
            />
            <Suspense>
                {isBusinessNameIdeasAvailable(locale) && (
                    <ExploreIdeas industryBusinessNameIdeasData={industryBusinessNameIdeasData} />
                )}
                <HowItWorks />
                <GoogleWorkspaceBanner />
            </Suspense>
        </>
    );
};

export default BusinessNameGeneratorPage;
